import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, api } from '@/lib/api';
import type { MarketingTemplate } from '@/lib/types';
import { Button, Field, Input, Modal, Textarea } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * Creates an `editorMode: 'html'` template from pasted or uploaded HTML through
 * `POST /marketing/templates/import-html`. The SERVER sanitises the markup
 * (scripts, iframes, `on*` attributes and `javascript:` URLs are stripped), so
 * nothing here edits what was pasted — a client-side copy of that rule would be
 * a second answer that drifts.
 */

export interface ImportHtmlModalProps {
  open: boolean;
  onClose: () => void;
  onImported: (template: MarketingTemplate) => void;
}

const MAX_FILE_BYTES = 2 * 1024 * 1024;
const KNOWN_KEYS = ['name', 'subject', 'html'] as const;

export function ImportHtmlModal({ open, onClose, onImported }: ImportHtmlModalProps) {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [fileNote, setFileNote] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setSubject('');
    setHtml('');
    setFileNote(null);
    setFieldErrors({});
    setFormError(null);
  }, [open]);

  function readFile(file: File) {
    setFileNote(null);
    if (file.size > MAX_FILE_BYTES) {
      setFieldErrors((prev) => ({ ...prev, html: 'That file is larger than 2 MB.' }));
      return;
    }
    file
      .text()
      .then((text) => {
        setHtml(text);
        setFileNote(file.name);
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next.html;
          return next;
        });
        if (!name.trim()) setName(file.name.replace(/\.html?$/i, ''));
      })
      .catch(() => setFieldErrors((prev) => ({ ...prev, html: 'That file could not be read.' })));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    event.stopPropagation();
    const local: Record<string, string> = {};
    if (!name.trim()) local.name = 'Enter a name.';
    if (!html.trim()) local.html = 'Paste HTML or choose a file.';
    setFieldErrors(local);
    setFormError(null);
    if (Object.keys(local).length > 0) return;

    setSaving(true);
    try {
      const response = await api.importMarketingTemplateHtml({
        name: name.trim(),
        html,
        ...(subject.trim() ? { subject: subject.trim() } : {}),
      });
      onImported(response.data);
    } catch (caught) {
      if (caught instanceof ApiError) {
        const routed = caught.routeFieldErrors(KNOWN_KEYS);
        setFieldErrors(routed.fields);
        setFormError(
          routed.unroutable.length > 0
            ? routed.unroutable.join(' ')
            : Object.keys(routed.fields).length > 0
              ? null
              : caught.message,
        );
      } else {
        setFormError('Could not reach the server. Nothing was imported.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Import HTML"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="import-html-form" loading={saving}>
            Import
          </Button>
        </>
      }
    >
      <form id="import-html-form" className="grid gap-4" onSubmit={(e) => void submit(e)} noValidate>
        {formError && (
          <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
            {formError}
          </Notice>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" required error={fieldErrors.name}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={name}
                maxLength={160}
                invalid={Boolean(fieldErrors.name)}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
          <Field label="Subject" error={fieldErrors.subject}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={subject}
                maxLength={500}
                invalid={Boolean(fieldErrors.subject)}
                onChange={(e) => setSubject(e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".html,.htm,text/html"
            className="sr-only"
            tabIndex={-1}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) readFile(file);
              e.target.value = '';
            }}
          />
          <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
            Choose .html file
          </Button>
          {fileNote && <span className="min-w-0 text-xs text-fg-muted [overflow-wrap:anywhere]">{fileNote}</span>}
        </div>
        <Field label="HTML" required error={fieldErrors.html}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              value={html}
              rows={14}
              spellCheck={false}
              invalid={Boolean(fieldErrors.html)}
              className="font-mono text-xs"
              onChange={(e) => setHtml(e.target.value)}
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}
